import type {
  EnrollmentCodeCommands,
  EnrollmentCodeStatusResponse,
  ResolveEnrollmentCodeResponse,
} from '@occ/contracts';
import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useBranding } from '../branding/BrandingProvider';
import { Alert, Button, Notice } from '../components/ui';
import {
  describeInstallCodeFailure,
  fetchInstallCodeStatus,
  resolveInstallCode,
  type InstallCodeFailure,
} from '../data/install';
import { timeAgo } from '../lib/format';
import {
  detectSystem,
  formatInstallCode,
  stripInstallCode,
  targetFor,
  type InstallTarget,
} from '../lib/installCode';

/**
 * La página pública de instalación (`/i`, plan de enrolamiento por código, §7).
 *
 * Es la mitad visible del plan: el técnico abre esta URL **en la caja del
 * cliente**, teclea seis caracteres y se lleva el comando ya armado. El problema
 * que resuelve no es de interfaz sino de transporte — hoy el comando nace en el
 * portátil del técnico y tiene que ejecutarse en otra máquina, y ese salto lo
 * hace un USB (§1). Aquí lo hace la red que la caja ya tiene, porque tenerla es
 * requisito para descargar el agente.
 *
 * Tres cosas que condicionan cómo está escrita esta pantalla:
 *
 * 1. **No hay sesión y no debe haberla.** Todo lo que se pinta sale de dos rutas
 *    públicas con el código en el cuerpo. Abrir el panel en la caja del cliente
 *    para copiar un token es exactamente lo que el plan evita.
 * 2. **El orden de los pasos no es decorativo.** El destino va antes del comando
 *    porque es la única oportunidad de que alguien se dé cuenta de que se
 *    equivocó de código; después ya lo pegó.
 * 3. **Windows 7 se queda con su comando y con esta página abierta en otro
 *    equipo.** El panel es Vite y React y no arranca en el IE11 de una caja
 *    vieja, así que la salida es el selector manual: el técnico abre `/i` en su
 *    portátil, elige "Windows 7" y se lleva el comando bueno. El aviso de
 *    navegador no soportado vive en `index.html`, fuera del bundle, porque tiene
 *    que verse justo cuando el bundle no corre.
 */

/** Cada cuánto se le pregunta al OCC si la caja ya apareció (§7, paso 6). */
const POLL_MS = 5_000;

const TARGETS: Array<{ id: InstallTarget; label: string }> = [
  { id: 'windows', label: 'Windows 10 / 11' },
  { id: 'windows7', label: 'Windows 7' },
  { id: 'linux', label: 'Linux' },
];

export function InstallCodePage() {
  const { branding } = useBranding();
  const productName = branding.productName || 'Omnia Control Center';

  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<InstallCodeFailure | null>(null);
  const [resolved, setResolved] = useState<{
    /** El texto exacto con el que se resolvió: es el que sigue preguntando `status`. */
    code: string;
    data: ResolveEnrollmentCodeResponse;
  } | null>(null);

  /**
   * Lo último que contestó `code/status`. Manda sobre la foto que dejó `resolve`
   * en lo que los dos dicen —caducidad y usos restantes— porque en un código de
   * veinte máquinas eso cambia mientras la pantalla está abierta, y un contador
   * congelado en "quedan 19" cuando ya van doce es peor que no ponerlo.
   */
  const [status, setStatus] = useState<EnrollmentCodeStatusResponse | null>(null);
  const [watchFailed, setWatchFailed] = useState(false);

  const detected = useMemo(
    () => detectSystem(typeof navigator === 'undefined' ? '' : navigator.userAgent),
    [],
  );
  const [chosen, setChosen] = useState<InstallTarget | null>(null);
  const target = chosen ?? targetFor(detected);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const code = stripInstallCode(draft);
    if (!code || busy) return;

    setBusy(true);
    setFailure(null);
    try {
      const data = await resolveInstallCode(code);
      setResolved({ code, data });
      setStatus(null);
      setWatchFailed(false);
    } catch (error) {
      setResolved(null);
      setFailure(describeInstallCodeFailure(error));
    } finally {
      setBusy(false);
    }
  };

  /**
   * El indicador en vivo: pregunta cada pocos segundos hasta que el código deja
   * de admitir máquinas (§7, paso 6).
   *
   * Se pregunta en bucle y no por el canal en vivo del panel porque ese canal
   * exige un JWT y aquí no hay sesión: la caja del cliente no tiene ninguna, que
   * es la premisa entera de esta página.
   *
   * **`live` es la señal de parar.** Un código de un solo uso se agota justo con
   * la instalación que el indicador está esperando, así que `status` contesta
   * igual cuando ya no sirve —al revés que `resolve`— y trae el interruptor.
   *
   * Un fallo corta el bucle en vez de reintentar: esta ruta comparte los límites
   * de `resolve` (§10), y una pestaña olvidada reintentando contra un OCC que
   * acaba de responder `429` es justo lo que el corte global existe para evitar.
   */
  useEffect(() => {
    if (!resolved) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      try {
        const next = await fetchInstallCodeStatus(resolved.code);
        if (cancelled) return;
        setStatus(next);
        if (!next.live) return;
      } catch {
        if (!cancelled) setWatchFailed(true);
        return;
      }
      timer = setTimeout(() => void poll(), POLL_MS);
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [resolved]);

  const restart = () => {
    setResolved(null);
    setStatus(null);
    setFailure(null);
    setWatchFailed(false);
    setDraft('');
  };

  return (
    <main className="mx-auto grid min-h-screen w-full max-w-3xl content-start gap-6 p-6 sm:p-10">
      <header className="grid gap-1">
        {branding.logoUrl ? (
          <img src={branding.logoUrl} alt={productName} className="h-7 w-auto max-w-[12rem]" />
        ) : (
          <span className="text-sm font-semibold tracking-tight opacity-70">{productName}</span>
        )}
        <h1 className="text-3xl font-semibold tracking-tight">Instalar un equipo</h1>
        <p className="text-muted-foreground text-base">
          Teclea el código que te dieron y esta página te entrega el comando listo para pegar en
          esta máquina.
        </p>
      </header>

      {/*
        Paso 1: el código, y nada más en pantalla hasta que resuelva. Lo que se
        pide aquí es tecleo a ciegas en la caja de un cliente, muchas veces con
        alguien dictando por teléfono; cualquier cosa al lado del campo compite
        con lo único que hay que hacer.
      */}
      <form onSubmit={(event) => void submit(event)} className="grid gap-3">
        <label className="grid gap-2">
          <span className="text-sm opacity-70">Código de instalación</span>
          {/*
            Escrito a mano y no con `<Field>`: este campo es el centro de la
            pantalla y necesita un tamaño y un interletrado que el input común no
            tiene. Monoespaciado porque se lee carácter a carácter, y con las
            correcciones del navegador apagadas — un autocorrector que convierta
            `4K7-M9Q` en otra cosa hace fallar el código sin que se note.
          */}
          <input
            value={draft}
            onChange={(event) => setDraft(formatInstallCode(event.target.value))}
            autoFocus
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="characters"
            spellCheck={false}
            inputMode="text"
            placeholder="4K7-M9Q"
            aria-label="Código de instalación"
            className="w-full rounded-lg bg-accent/40 px-4 py-5 text-center font-mono text-4xl tracking-[0.2em] uppercase shadow-sm outline-none focus:shadow-[0_0_0_2px_var(--ring)]"
          />
        </label>

        <Button type="submit" variant="primary" size="lg" disabled={busy || !draft.trim()}>
          {busy ? 'Comprobando el código…' : 'Continuar'}
        </Button>

        {failure?.kind === 'code' && <Alert>{failure.message}</Alert>}
        {failure?.kind === 'service' && (
          <Notice tone="warn" title="El código puede estar bien; el que no puede ahora es el OCC">
            {failure.message}
          </Notice>
        )}
      </form>

      {resolved && (
        <>
          <Destination data={resolved.data} status={status} />
          <CommandCard
            commands={resolved.data.commands}
            target={target}
            detected={chosen === null ? detected : 'chosen'}
            onTarget={setChosen}
          />
          <LiveIndicator status={status} failed={watchFailed} />
          <Troubleshooting target={target} />

          <div>
            <Button variant="ghost" onClick={restart}>
              Usar otro código
            </Button>
          </div>
        </>
      )}
    </main>
  );
}

/**
 * Paso 2: a dónde instala este código, **antes** del comando.
 *
 * Va antes y no después porque es la única oportunidad de que alguien se dé
 * cuenta de que se equivocó de código: después de pegarlo, la máquina ya está en
 * el portafolio del cliente que no era y lo que queda es darla de baja.
 */
function Destination({
  data,
  status,
}: {
  data: ResolveEnrollmentCodeResponse;
  status: EnrollmentCodeStatusResponse | null;
}) {
  const expiresAt = status?.expiresAt ?? data.expiresAt;
  const remaining = status?.remainingUses ?? data.remainingUses;

  return (
    <section className="grid gap-2 rounded-lg bg-accent/40 p-5 shadow-sm">
      <p className="text-lg">
        Este código instala en{' '}
        <strong className="font-semibold">
          {data.clientName} → {data.branchName}
        </strong>
        .
      </p>
      <p className="text-sm opacity-70">
        Caduca {timeAgo(expiresAt)} · {remaining === 1 ? 'queda 1 uso' : `quedan ${remaining} usos`}
        {data.suggestedAlias && <> · el equipo se llamará {data.suggestedAlias}</>}
      </p>
      <p className="text-sm opacity-70">
        Si no es el cliente que esperabas, no sigas: pide el código correcto en el panel.
      </p>
    </section>
  );
}

/** Cómo se llegó al sistema que se está mostrando, para decirlo sin mentir. */
type TargetOrigin = InstallTarget | 'macos' | 'unknown' | 'chosen';

/**
 * Pasos 3, 4 y 5: el comando del sistema que toca, cómo abrir la consola donde
 * se pega, y el botón de copiar.
 *
 * **El selector manual está al lado del comando y no escondido**, porque la
 * detección por `User-Agent` acierta casi siempre y "casi siempre" no basta
 * cuando el error es invisible: el comando equivocado no da un error rojo, se
 * pega en la consola que no era y nadie se entera hasta que la caja no aparece en
 * el panel.
 */
function CommandCard({
  commands,
  target,
  detected,
  onTarget,
}: {
  commands: EnrollmentCodeCommands;
  target: InstallTarget;
  detected: TargetOrigin;
  onTarget: (target: InstallTarget) => void;
}) {
  const command = target === 'windows7' ? commands.windows7 : commands[target];
  const unavailable = target === 'windows7' ? commands.windows7Unavailable : null;

  return (
    <section className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold tracking-tight">El comando para esta máquina</h2>
        <div
          role="group"
          aria-label="Sistema operativo de la máquina"
          className="flex flex-wrap gap-1 rounded-lg bg-accent/40 p-1"
        >
          {TARGETS.map((option) => (
            <Button
              key={option.id}
              variant={option.id === target ? 'primary' : 'ghost'}
              size="sm"
              aria-pressed={option.id === target}
              onClick={() => onTarget(option.id)}
            >
              {option.label}
            </Button>
          ))}
        </div>
      </div>

      <DetectionNotice detected={detected} />

      {command === null ? (
        <Notice tone="warn" title="No hay comando de Windows 7 ahora mismo">
          {unavailable ??
            'El comando de Windows 7 lleva pineados los hashes de la versión publicada, y este OCC ' +
              'no los puede resolver en este momento.'}{' '}
          Pídelo en el panel, que arma el mismo comando desde la pantalla de alta.
        </Notice>
      ) : (
        <>
          <Console target={target} />
          <CommandToCopy command={command} />
        </>
      )}
    </section>
  );
}

/** Lo que la detección dedujo, dicho solo cuando cambia lo que hay que hacer. */
function DetectionNotice({ detected }: { detected: TargetOrigin }) {
  if (detected === 'macos') {
    return (
      <Notice tone="warn" title="Esta máquina parece un Mac">
        El agente todavía no corre en macOS, así que ninguno de estos comandos vale aquí. Si estás
        mirando esta página desde tu Mac para instalar en otra caja, elige arriba el sistema de esa
        caja.
      </Notice>
    );
  }

  if (detected === 'unknown') {
    return (
      <Notice tone="warn" title="No pudimos reconocer este sistema">
        Elige arriba el de la máquina donde vas a instalar. El comando de cada uno es distinto y el
        equivocado no falla de forma visible: no pasa nada hasta que el equipo no aparece en el
        panel.
      </Notice>
    );
  }

  if (detected === 'chosen') return null;

  return (
    <p className="text-sm opacity-60">
      Detectamos esta máquina como <strong className="font-medium">{labelOf(detected)}</strong>. Si
      no es así, cámbialo arriba antes de copiar.
    </p>
  );
}

const labelOf = (target: InstallTarget): string =>
  TARGETS.find((option) => option.id === target)?.label ?? target;

/**
 * Paso 4: dónde se pega esto, con las teclas exactas.
 *
 * Las teclas y no "abre una consola como administrador": quien está delante de la
 * caja de un cliente no siempre sabe dónde está el menú de esa versión de
 * Windows, y ese es el paso donde se pierde la visita.
 */
function Console({ target }: { target: InstallTarget }) {
  if (target === 'linux') {
    return (
      <div className="grid gap-1.5 text-sm">
        <p className="font-medium">1. Abre una terminal con tu usuario normal.</p>
        <p className="opacity-70">
          No hace falta entrar como root: el comando ya lleva <Key>sudo</Key> dentro y te va a pedir
          tu contraseña al pegarlo.
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-1.5 text-sm">
      <p className="font-medium">1. Abre una consola de PowerShell como administrador.</p>
      {target === 'windows7' ? (
        <p className="opacity-70">
          <Key>Win</Key>, escribe <Key>powershell</Key>, y en el resultado haz clic derecho →{' '}
          <em>Ejecutar como administrador</em>. También sirve <Key>Win</Key>+<Key>R</Key> →{' '}
          <Key>powershell</Key> → <Key>Ctrl</Key>+<Key>Shift</Key>+<Key>Enter</Key>.
        </p>
      ) : (
        <p className="opacity-70">
          <Key>Win</Key>+<Key>X</Key> → <em>Terminal (Administrador)</em>. Si ese menú no lo ofrece:{' '}
          <Key>Win</Key>+<Key>R</Key> → <Key>powershell</Key> → <Key>Ctrl</Key>+<Key>Shift</Key>+
          <Key>Enter</Key>.
        </p>
      )}
      <p className="opacity-70">
        Tiene que decir <em>Administrador</em> en el título de la ventana. Sin eso el instalador
        para antes de tocar nada.
      </p>
    </div>
  );
}

function Key({ children }: { children: string }) {
  return (
    <kbd className="rounded bg-black/10 px-1.5 py-0.5 font-mono text-xs dark:bg-white/15">
      {children}
    </kbd>
  );
}

/**
 * Paso 5: el botón de copiar, que es el elemento más grande de la pantalla.
 *
 * Lo es a propósito: todo lo demás son cosas que se leen una vez, y esto es lo
 * único que hay que **hacer**. El texto del comando queda encima y seleccionable
 * porque el portapapeles no siempre está disponible —un OCC servido por `http`
 * sin TLS no tiene `navigator.clipboard`—, y en ese caso lo que queda es
 * seleccionarlo y copiarlo a mano, que es justo lo que hace el respaldo.
 */
function CommandToCopy({ command }: { command: string }) {
  const [copied, setCopied] = useState(false);
  const [manual, setManual] = useState(false);
  const box = useRef<HTMLPreElement>(null);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 4_000);
    return () => clearTimeout(timer);
  }, [copied]);

  const selectAll = useCallback(() => {
    const node = box.current;
    if (!node || typeof window.getSelection !== 'function') return;
    const range = document.createRange();
    range.selectNodeContents(node);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setManual(false);
    } catch {
      setCopied(false);
      setManual(true);
      selectAll();
    }
  };

  return (
    <div className="grid gap-3">
      <p className="text-sm font-medium">2. Pega esto y dale a Enter.</p>
      <pre
        ref={box}
        onClick={selectAll}
        className="overflow-x-auto rounded-lg bg-black/5 p-4 font-mono text-sm leading-6 select-all dark:bg-white/5"
      >
        {command}
      </pre>
      <Button
        variant="primary"
        onClick={() => void copy()}
        className="h-auto w-full rounded-lg py-6 text-xl font-semibold shadow-lg"
      >
        {copied ? 'Copiado ✓ — ahora pégalo en la consola' : 'Copiar el comando'}
      </Button>
      {manual && (
        <Alert>
          Este navegador no deja copiar al portapapeles. El comando quedó seleccionado arriba:
          cópialo con Ctrl+C.
        </Alert>
      )}
    </div>
  );
}

/**
 * Paso 6: la confirmación en vivo.
 *
 * Es lo que cierra la visita sin una llamada. Hasta ahora, el técnico que pegaba
 * el comando no tenía forma de saber si el equipo había entrado sin abrir el
 * panel —que en la caja del cliente no debe abrir— o sin llamar a alguien que lo
 * mirase.
 */
function LiveIndicator({
  status,
  failed,
}: {
  status: EnrollmentCodeStatusResponse | null;
  failed: boolean;
}) {
  return (
    <section aria-live="polite" className="grid gap-2 rounded-lg bg-accent/40 p-5 shadow-sm">
      <h2 className="text-sm font-semibold tracking-tight">¿Ya entró el equipo?</h2>

      {failed ? (
        <p className="text-sm opacity-70">
          Dejamos de consultarlo desde aquí. Eso no dice nada de la instalación: si el comando
          terminó bien, el equipo está en el panel. Míralo ahí.
        </p>
      ) : status === null ? (
        <p className="text-sm opacity-60">Esperando a que la caja aparezca…</p>
      ) : status.enrolled.length === 0 ? (
        <p className="text-sm opacity-60">
          Todavía no ha entrado ninguna máquina con este código. Deja esta pestaña abierta: en
          cuanto el instalador termine, aparece aquí.
        </p>
      ) : (
        <ul className="grid gap-1">
          {status.enrolled.map((node) => (
            <li key={`${node.alias}-${node.at.toISOString()}`} className="text-sm">
              <strong className="font-semibold">Equipo enrolado</strong> — {node.alias},{' '}
              {timeAgo(node.at)}
            </li>
          ))}
        </ul>
      )}

      {status !== null && !status.live && status.enrolled.length > 0 && (
        <p className="text-xs opacity-60">
          El código ya no admite más máquinas, así que dejamos de consultarlo.
        </p>
      )}
    </section>
  );
}

/**
 * Paso 7: qué hacer cuando no sale a la primera.
 *
 * Desplegable y debajo porque el caso normal no lo necesita, y completo porque el
 * caso que lo necesita está en una tienda con el cliente delante. Lo del
 * antivirus es §9 del plan y las rutas exactas salen de
 * `docs/tech-specs/M2-antivirus.md`: que el técnico las tenga aquí es la
 * diferencia entre pedírselas a TI del cliente en el momento y volver otro día.
 */
function Troubleshooting({ target }: { target: InstallTarget }) {
  const windows = target !== 'linux';

  return (
    <details className="rounded-lg bg-accent/30 p-5 shadow-sm">
      <summary className="cursor-pointer text-sm font-semibold tracking-tight">
        No me funcionó — qué mirar
      </summary>

      <div className="mt-4 grid gap-5 text-sm">
        {windows && (
          <>
            <div className="grid gap-1.5">
              <h3 className="font-medium">El antivirus se llevó el agente</h3>
              <p className="opacity-70">
                Si el instalador dice que el binario quedó en cuarentena, no lo reintentes: va a
                pasar otra vez. El instalador aplica por su cuenta las exclusiones de Microsoft
                Defender antes de descargar y comprueba que quedaron puestas, pero en un cliente con
                Defender gestionado por Intune o GPO las exclusiones locales se revierten en el
                siguiente ciclo de directiva y hay que pedírselas a TI del cliente:
              </p>
              <pre className="overflow-x-auto rounded bg-black/5 p-3 font-mono text-xs leading-5 dark:bg-white/5">
                {[
                  'Exclusiones de ruta:',
                  '  C:\\Program Files\\OmniaOS\\occ-agent',
                  '  C:\\ProgramData\\OmniaOS\\occ-agent',
                  'Exclusiones de proceso:',
                  '  occ-agent.exe',
                ].join('\n')}
              </pre>
              <p className="opacity-70">
                En Intune están en Endpoint security → Antivirus → perfil de Microsoft Defender
                Antivirus. En GPO, en Configuración del equipo → Plantillas administrativas →
                Componentes de Windows → Antivirus de Microsoft Defender → Exclusiones. Conviene
                pedir dos cosas más que rompen la instalación de formas poco obvias: añadir{' '}
                <code className="font-mono text-xs">occ-agent.exe</code> a las aplicaciones
                permitidas del <em>acceso controlado a carpetas</em>, y revisar las reglas ASR de
                PowerShell. Con un antivirus de terceros —Bitdefender, ESET, Kaspersky, Sophos,
                Webroot— lo que hay que pedir es lo mismo: excluir esas dos carpetas y el proceso,
                permitir su salida al 443 del OCC, y añadirlo como aplicación de confianza si el
                producto tiene control de aplicaciones o HIPS.
              </p>
            </div>

            <div className="grid gap-1.5">
              <h3 className="font-medium">
                Dice que <code className="font-mono text-xs">irm</code> no se reconoce
              </h3>
              <p className="opacity-70">
                Esa caja tiene PowerShell 2.0, que es lo que traen los Windows 7 sin actualizar.
                Elige «Windows 7» en el selector de arriba y usa el comando de ahí, que está escrito
                para esa versión.
              </p>
            </div>

            <div className="grid gap-1.5">
              <h3 className="font-medium">La directiva de ejecución lo bloquea</h3>
              <p className="opacity-70">
                Pegar el comando en la consola no debería toparse con ella —lo que se ejecuta no es
                un archivo—, así que si aparece es que hay una directiva de equipo. En la misma
                consola de administrador:
              </p>
              <pre className="overflow-x-auto rounded bg-black/5 p-3 font-mono text-xs leading-5 dark:bg-white/5">
                Set-ExecutionPolicy -Scope Process -ExecutionPolicy Bypass
              </pre>
              <p className="opacity-70">
                Vale solo para esa ventana, que es lo que hay que querer: no deja la máquina del
                cliente más abierta de lo que estaba.
              </p>
            </div>

            <div className="grid gap-1.5">
              <h3 className="font-medium">De plano no puedo pegar en la consola</h3>
              <p className="opacity-70">
                Guarda el comando en un archivo <code className="font-mono text-xs">.ps1</code> en
                esa misma máquina y ejecútalo con estas dos líneas. Ojo: ese archivo lleva dentro
                una credencial viva, así que bórralo en cuanto termine.
              </p>
              <pre className="overflow-x-auto rounded bg-black/5 p-3 font-mono text-xs leading-5 dark:bg-white/5">
                {[
                  'Unblock-File .\\occ-instalar.ps1',
                  'powershell -ExecutionPolicy Bypass -File .\\occ-instalar.ps1',
                ].join('\n')}
              </pre>
            </div>
          </>
        )}

        <div className="grid gap-1.5">
          <h3 className="font-medium">Hay un proxy o un filtro de URL de por medio</h3>
          <p className="opacity-70">
            La descarga tiene que llegar al dominio de este OCC por el 443. Si el comando se queda
            colgado o falla al descargar, prueba con la URL larga: la ruta{' '}
            <code className="font-mono text-xs">/i.ps1</code> es un alias de{' '}
            <code className="font-mono text-xs">/install.ps1</code>, y{' '}
            <code className="font-mono text-xs">/i.sh</code> lo es de{' '}
            <code className="font-mono text-xs">/install.sh</code>. Sirven exactamente lo mismo, y
            un filtro corporativo que bloquee una ruta corta y desconocida suele dejar pasar la
            otra.
          </p>
        </div>

        <div className="grid gap-1.5">
          <h3 className="font-medium">Nada de esto es</h3>
          <p className="opacity-70">
            El flujo de siempre sigue entero: desde el panel se emite un token largo con su comando
            ya armado, y no pasa por esta página.
          </p>
        </div>
      </div>
    </details>
  );
}
